import * as THREE from 'https://esm.sh/three@0.181.0';
import { OrbitControls } from 'https://esm.sh/three@0.181.0/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'https://esm.sh/three@0.181.0/examples/jsm/loaders/GLTFLoader.js';

const STORAGE_KEY = 'lumira.roomBuilder.layout.v1';
const SAVED_KEY = 'lumira.roomBuilder.savedPieces.v1';
const BUILD_ID = 'ux-20260602-clean';
const FLOOR_COLORS = {
  'light-wood': 0xcaa878,
  'dark-wood': 0x5b3a24,
  'grey-wood': 0x8f918b,
  concrete: 0xaaa69d,
  carpet: 0xc9c1b5
};
const BAD_GLB_PRODUCT_IDS = new Set([
  '9222978863358',
  '9222813810942',
  '9222853525758',
  '9222853296382',
  '9222958776574',
  '9222834225406',
  '9222892257534',
  '9222833144062',
  '9222853755134',
  '9222853624062',
  '9222873612542',
  '9222935052542',
  '9222935150846',
  '9222822559998',
  '9222935380222',
  '9222844645630',
  '9222935609598',
  '9222933905662',
  '9222844547326',
  '9222825967870',
  '9222834192638',
  '9222836289790',
  '9222935347454',
  '9222852935934',
  '9222909493502',
  '9222973456638',
  '9222836093182',
  '9222987055358',
  '9222935019774',
  '9222935183614',
  '9222933971198',
  '9222958579966',
  '9222958809342',
  '9222841630974',
  '9222958088446',
  '9222934069502'
]);

function $(root, selector) {
  return root.querySelector(selector);
}

function feet(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function money(value) {
  return Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatFeet(value) {
  const totalInches = Math.max(0, Math.round(Number(value || 0) * 12));
  const ft = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  if (!inches) return `${ft} ft`;
  if (!ft) return `${inches} in`;
  return `${ft} ft ${inches} in`;
}

function formatDimsFt(dims) {
  return `${formatFeet(dims.width)} W x ${formatFeet(dims.depth)} D x ${formatFeet(dims.height)} H`;
}

function roomLabel(room) {
  return `${formatFeet(room.width)} W x ${formatFeet(room.length)} L x ${formatFeet(room.height)} ceiling`;
}

function norm(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function parseDimensions(raw) {
  if (raw && typeof raw === 'object') {
    const width = Number(raw.width_in || raw.width || raw.w || 0);
    const depth = Number(raw.depth_in || raw.depth || raw.d || raw.length || 0);
    const height = Number(raw.height_in || raw.height || raw.h || 0);
    return width && depth && height ? { widthIn: width, depthIn: depth, heightIn: height } : null;
  }
  const text = String(raw || '');
  const match = text.match(/(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?\s*w\s*x?\s*(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?\s*d\s*x?\s*(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?\s*h/i)
    || text.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  return { widthIn: Number(match[1]), depthIn: Number(match[2]), heightIn: Number(match[3]) };
}

function dimsToFeet(product) {
  const dims = parseDimensions(product.dimensions_parsed || product.dimensions);
  if (!dims) return null;
  const result = {
    width: dims.widthIn / 12,
    depth: dims.depthIn / 12,
    height: dims.heightIn / 12
  };
  return {
    ...result,
    label: formatDimsFt(result),
    inchesLabel: `${dims.widthIn}" W x ${dims.depthIn}" D x ${dims.heightIn}" H`
  };
}

function productText(product) {
  return norm([
    product.title,
    product.collection,
    product.kind,
    (product.colors || []).join(' '),
    (product.materials || []).join(' '),
    (product.styles || []).join(' ')
  ].join(' '));
}

function productImage(product) {
  return product.image || product.image_url || product.featured_image || product.featuredImage || '';
}

function productKey(product) {
  return String(product.product_id || product.id || product.variant_id || '');
}

function hasUsableGlb(product) {
  return Boolean(product && product.glb_url && (product.trueSizeFt || dimsToFeet(product)) && !BAD_GLB_PRODUCT_IDS.has(productKey(product)));
}

function sortPlaceable(a, b) {
  const ag = hasUsableGlb(a) ? 1 : 0;
  const bg = hasUsableGlb(b) ? 1 : 0;
  if (ag !== bg) return bg - ag;
  return String(a.title || '').localeCompare(String(b.title || ''));
}

class RoomBuilder {
  constructor(root) {
    this.root = root;
    this.canvas = $(root, '[data-lrb-canvas]');
    this.stage = $(root, '[data-lrb-stage]');
    this.status = $(root, '[data-lrb-status]');
    this.empty = $(root, '[data-lrb-empty]');
    this.roomReady = $(root, '[data-lrb-room-ready]');
    this.productsNode = $(root, '[data-lrb-products]');
    this.preview = $(root, '[data-lrb-preview]');
    this.previewMeta = $(root, '[data-lrb-preview-meta]');
    this.pieceCount = $(root, '[data-lrb-piece-count]');
    this.dimensionStatus = $(root, '[data-lrb-dimension-status]');
    this.capacityLabel = $(root, '[data-lrb-capacity-label]');
    this.capacityBar = $(root, '[data-lrb-capacity-bar]');
    this.autoRotateButton = $(root, '[data-lrb-auto-rotate]');
    this.loader = new GLTFLoader();
    this.clock = new THREE.Clock();
    this.pointer = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.room = { width: 12, length: 14, height: 8, wall: '#f2eee7', floor: 'light-wood' };
    this.capacityTarget = Math.max(0.1, Math.min(0.95, Number(root.dataset.capacityTarget || 0.75)));
    this.catalog = [];
    this.saved = this.readLocalSaved();
    this.placed = [];
    this.selected = null;
    this.dragging = false;
    this.dragOffset = new THREE.Vector3();
    this.lastDragPosition = new THREE.Vector3();
    this.roomReadyTimer = null;
    this.mode = 'saved';
    this.initScene();
    this.bind();
    this.loadCapacityAdvice();
    this.loadCatalog().then(() => {
      this.applyUrlParams();
      this.renderProducts();
      this.restoreLayout(false);
      this.updateCapacity();
    });
    this.animate();
  }

  initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf4f1ec);
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.05, 200);
    this.camera.position.set(10, 9, 12);
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.shadowMap.enabled = false;
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.target.set(0, 1.8, 0);
    this.controls.enableDamping = true;
    this.controls.enableRotate = false;
    this.controls.enablePan = true;
    this.controls.enableZoom = true;
    this.controls.autoRotate = false;
    this.controls.autoRotateSpeed = 2.4;
    this.controls.maxPolarAngle = Math.PI * 0.49;
    this.roomGroup = new THREE.Group();
    this.itemGroup = new THREE.Group();
    this.scene.add(this.roomGroup, this.itemGroup);
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x7a6f62, 1.8));
    const sun = new THREE.DirectionalLight(0xffffff, 2.4);
    sun.position.set(6, 10, 8);
    sun.castShadow = false;
    this.scene.add(sun);
    this.floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.generateRoom();
    this.resize();
    this.controls.addEventListener('change', () => this.constrainCamera());
    window.addEventListener('resize', () => this.resize());
  }

  bind() {
    $(this.root, '[data-lrb-room-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      this.room.width = feet($(this.root, '[data-lrb-width]').value, 12);
      this.room.length = feet($(this.root, '[data-lrb-length]').value, 14);
      this.room.height = feet($(this.root, '[data-lrb-height]').value, 8);
      this.room.wall = $(this.root, '[data-lrb-wall]').value;
      this.room.floor = $(this.root, '[data-lrb-floor]').value;
      this.generateRoom();
      this.constrainAll();
      this.loadCapacityAdvice();
      this.updateCapacity();
      this.setStatus(`${roomLabel(this.room)} is ready. Add saved pieces or search placeable products.`);
    });
    $(this.root, '[data-lrb-photo-form]').addEventListener('submit', (event) => this.analyzePhoto(event));
    $(this.root, '[data-lrb-search-button]').addEventListener('click', () => {
      this.setMode('results');
      this.renderProducts();
    });
    $(this.root, '[data-lrb-search]').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.setMode('results');
        this.renderProducts();
      }
    });
    this.root.querySelectorAll('[data-lrb-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        this.setMode(button.dataset.lrbTab);
        this.renderProducts();
      });
    });
    $(this.root, '[data-lrb-rotate-left]').addEventListener('click', () => this.rotateSelected(-15));
    $(this.root, '[data-lrb-rotate-right]').addEventListener('click', () => this.rotateSelected(15));
    $(this.root, '[data-lrb-reset-view]').addEventListener('click', () => {
      this.pauseAutoRotate('Auto-rotate is off. View reset to the room interior.');
      this.setInteriorCamera();
    });
    $(this.root, '[data-lrb-snap]').addEventListener('click', () => this.snapSelected());
    if (this.autoRotateButton) this.autoRotateButton.addEventListener('click', () => this.toggleAutoRotate());
    $(this.root, '[data-lrb-remove]').addEventListener('click', () => this.removeSelected());
    $(this.root, '[data-lrb-restore]').addEventListener('click', () => this.restoreLayout(true));
    $(this.root, '[data-lrb-save-layout]').addEventListener('click', () => this.saveLayout());
    $(this.root, '[data-lrb-checkout]').addEventListener('click', () => this.checkout());
    this.canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.canvas.addEventListener('pointerup', () => this.onPointerUp());
    this.canvas.addEventListener('pointercancel', () => this.onPointerUp());
  }

  async loadCatalog() {
    const local = await fetch(this.root.dataset.catalogUrl, { cache: 'force-cache' }).then((res) => res.json()).catch(() => ({ products: [] }));
    let products = Array.isArray(local.products) ? local.products : [];
    const remote = await fetch(`${this.root.dataset.sophiBase}/sophi/room-builder/catalog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: '' })
    }).then((res) => res.ok ? res.json() : null).catch(() => null);
    if (remote && Array.isArray(remote.products) && remote.products.length) products = remote.products;
    this.catalog = products.map((product) => {
      const dims = dimsToFeet(product);
      return Object.assign({}, product, {
        searchText: productText(product),
        trueSizeFt: dims,
        dimension_status: dims ? 'verified' : 'missing'
      });
    }).sort(sortPlaceable);
    await this.loadSavedFromShopify();
  }

  async loadCapacityAdvice() {
    const result = await fetch(`${this.root.dataset.sophiBase}/sophi/room-builder/capacity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        width_ft: this.room.width,
        length_ft: this.room.length,
        height_ft: this.room.height,
        target_coverage: Number(this.root.dataset.capacityTarget || 0.75)
      })
    }).then((res) => res.ok ? res.json() : null).catch(() => null);
    const advised = Number(result && result.max_coverage_ratio);
    if (Number.isFinite(advised)) {
      this.capacityTarget = Math.max(0.1, Math.min(0.95, advised));
      this.updateCapacity();
    }
  }

  applyUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const width = feet(params.get('w'), this.room.width);
    const length = feet(params.get('l'), this.room.length);
    const height = feet(params.get('h'), this.room.height);
    this.room.width = width;
    this.room.length = length;
    this.room.height = height;
    $(this.root, '[data-lrb-width]').value = width;
    $(this.root, '[data-lrb-length]').value = length;
    $(this.root, '[data-lrb-height]').value = height;
    this.generateRoom();
    this.updateCapacity();
    const ids = String(params.get('product_ids') || '').split(',').map((row) => row.trim()).filter(Boolean);
    const variantIds = String(params.get('variant_ids') || params.get('variants') || '').split(',').map((row) => row.trim()).filter(Boolean);
    [...ids, ...variantIds].forEach((id, index) => {
      const product = this.catalog.find((row) => String(row.id) === id || String(row.product_id) === id || String(row.variant_id) === id);
      if (product) this.placeProduct(product, { x: -this.room.width / 4 + index * 1.2, z: -this.room.length / 4 + index * 0.8 });
    });
  }

  generateRoom() {
    this.roomGroup.clear();
    const wallColor = new THREE.Color(this.room.wall || '#f2eee7');
    const wallMat = new THREE.MeshStandardMaterial({ color: wallColor, roughness: 0.86 });
    const floorMat = new THREE.MeshStandardMaterial({ color: FLOOR_COLORS[this.room.floor] || FLOOR_COLORS['light-wood'], roughness: 0.74 });
    const ceilMat = new THREE.MeshStandardMaterial({ color: 0xf8f4ed, roughness: 0.9 });
    const floor = new THREE.Mesh(new THREE.BoxGeometry(this.room.width, 0.04, this.room.length), floorMat);
    floor.position.y = -0.02;
    floor.receiveShadow = false;
    floor.userData.floor = true;
    this.roomGroup.add(floor);
    const back = this.wallMesh(this.room.width, this.room.height, wallMat);
    back.position.set(0, this.room.height / 2, -this.room.length / 2);
    this.roomGroup.add(back);
    const front = this.wallMesh(this.room.width, this.room.height, wallMat);
    front.position.set(0, this.room.height / 2, this.room.length / 2);
    front.rotation.y = Math.PI;
    front.material = wallMat.clone();
    front.material.transparent = true;
    front.material.opacity = 0.28;
    this.roomGroup.add(front);
    const left = this.wallMesh(this.room.length, this.room.height, wallMat);
    left.position.set(-this.room.width / 2, this.room.height / 2, 0);
    left.rotation.y = Math.PI / 2;
    this.roomGroup.add(left);
    const right = this.wallMesh(this.room.length, this.room.height, wallMat);
    right.position.set(this.room.width / 2, this.room.height / 2, 0);
    right.rotation.y = -Math.PI / 2;
    this.roomGroup.add(right);
    const ceiling = new THREE.Mesh(new THREE.BoxGeometry(this.room.width, 0.03, this.room.length), ceilMat);
    ceiling.position.y = this.room.height;
    ceiling.material.transparent = true;
    ceiling.material.opacity = 0.18;
    this.roomGroup.add(ceiling);
    this.addGrid();
    this.setInteriorCamera();
    this.updateDimensionCopy();
    this.showRoomReady();
  }

  wallMesh(width, height, material) {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.05), material);
    mesh.receiveShadow = false;
    return mesh;
  }

  addGrid() {
    const grid = new THREE.GridHelper(Math.max(this.room.width, this.room.length), Math.max(this.room.width, this.room.length), 0x6f6a61, 0xcac2b7);
    grid.position.y = 0.01;
    this.roomGroup.add(grid);
  }

  async analyzePhoto(event) {
    event.preventDefault();
    const file = $(this.root, '[data-lrb-photo]').files[0];
    if (!file) {
      this.setStatus('Choose a room photo first.');
      return;
    }
    const body = new FormData();
    body.append('photo', file);
    body.append('session_id', `room-builder-${Date.now()}`);
    this.setStatus('Sophi is estimating dimensions from the photo...');
    const result = await fetch(`${this.root.dataset.sophiBase}/sophi/analyze-room`, { method: 'POST', body })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .catch(() => null);
    if (!result) {
      this.dimensionStatus.textContent = 'Photo fallback';
      this.setStatus('Sophi could not extract reliable dimensions. Manual entry is ready.');
      return;
    }
    const dims = result.adjusted_dimensions || result.parametric_room || (result.room_analysis || {}).estimated_dimensions || {};
    this.room.width = feet(dims.width_ft || dims.width, this.room.width);
    this.room.length = feet(dims.length_ft || dims.depth_ft || dims.length, this.room.length);
    this.room.height = feet(dims.height_ft || dims.ceiling_height_ft || dims.height, this.room.height);
    $(this.root, '[data-lrb-width]').value = this.room.width;
    $(this.root, '[data-lrb-length]').value = this.room.length;
    $(this.root, '[data-lrb-height]').value = this.room.height;
    this.generateRoom();
    this.dimensionStatus.textContent = 'Sophi estimate';
    this.setStatus(result.sophi_message || `Sophi estimated ${roomLabel(this.room)}.`);
  }

  renderProducts() {
    const q = norm($(this.root, '[data-lrb-search]').value);
    let products = [];
    if (this.mode === 'results') {
      products = q ? this.catalog.filter(hasUsableGlb) : [];
    }
    if (this.mode === 'saved') products = this.saved.filter(hasUsableGlb);
    if (this.mode === 'room') products = this.placed.map((item) => item.product);
    if (q) products = products.filter((product) => (product.searchText || productText(product)).includes(q));
    this.productsNode.innerHTML = '';
    products.sort(sortPlaceable).slice(0, 24).forEach((product) => this.productsNode.appendChild(this.productCard(product)));
    if (!products.length) {
      const empty = document.createElement('p');
      empty.className = 'lrb-preview__meta';
      if (this.mode === 'saved') {
        empty.textContent = 'No saved placeable pieces yet. Search Lumira products with 3D models, then save favorites.';
      } else if (this.mode === 'results' && !q) {
        empty.textContent = 'Search by product name, room, color, material, or collection. Results only include usable 3D models.';
      } else {
        empty.textContent = 'No usable 3D matches found. Try another product name, color, material, or collection.';
      }
      this.productsNode.appendChild(empty);
    }
  }

  productCard(product) {
    const card = document.createElement('article');
    card.className = 'lrb-product';
    card.dataset.productId = productKey(product);
    card.dataset.placeable = hasUsableGlb(product) ? 'true' : 'false';
    card.dataset.hasGlb = product.glb_url ? 'true' : 'false';
    const dims = product.trueSizeFt || dimsToFeet(product);
    card.innerHTML = `
      <img alt="${product.title || 'Lumira product'}" src="${productImage(product)}" loading="lazy" decoding="async" referrerpolicy="no-referrer">
      <div>
        <h3>${product.title || 'Lumira piece'}</h3>
        <p>${money(product.price)} ${dims ? `| ${dims.label}` : '| dimensions needed'}</p>
        <div class="lrb-product__actions">
          <button type="button" data-action="place">Place</button>
          <button type="button" data-action="save">Save</button>
          <button type="button" data-action="preview">Preview</button>
        </div>
      </div>`;
    card.querySelector('[data-action="place"]').addEventListener('click', () => this.placeProduct(product));
    card.querySelector('[data-action="save"]').addEventListener('click', () => this.savePiece(product));
    card.querySelector('[data-action="preview"]').addEventListener('click', () => this.previewProduct(product));
    const image = card.querySelector('img');
    image.addEventListener('error', () => {
      image.classList.add('is-missing');
    }, { once: true });
    return card;
  }

  placeProduct(product, preferred = {}) {
    if (!hasUsableGlb(product)) {
      this.setStatus(`${product.title || 'This piece'} is not available for true-scale room placement yet.`);
      return;
    }
    const dims = product.trueSizeFt || dimsToFeet(product);
    if (!dims) {
      this.setStatus(`${product.title} is missing measured dimensions and cannot be true-scale placed yet.`);
      return;
    }
    if (!product.glb_url) {
      this.setStatus(`${product.title} is missing a GLB model.`);
      return;
    }
    const nextCoverage = this.coverageRatio(dims);
    let capacityWarning = '';
    if (nextCoverage > this.capacityTarget) {
      this.updateCapacity(nextCoverage);
      const allowed = Math.round(this.capacityTarget * 100);
      const next = Math.round(nextCoverage * 100);
      capacityWarning = `This room is getting full. Sophi's design radar recommends staying near ${allowed}% floor coverage, and ${product.title} brings it to ${next}%. I placed it so we can solve the layout with smarter moves: vertical storage, dual-function pieces, wall placement, or scaled companions.`;
    }
    const placeholder = this.placeholder(dims, product);
    const item = {
      id: `${product.id || product.variant_id}-${Date.now()}`,
      product,
      mesh: placeholder,
      width: dims.width,
      depth: dims.depth,
      height: dims.height,
      rotation: 0
    };
    placeholder.userData.item = item;
    placeholder.position.set(preferred.x || 0, 0, preferred.z || 0);
    this.itemGroup.add(placeholder);
    this.placed.push(item);
    this.selected = item;
    this.loadModel(item);
    this.constrainItem(item);
    this.updateSelection();
    this.updateCapacity();
    this.setStatus(capacityWarning || `${product.title} placed at true scale: ${dims.label}.`);
  }

  placeholder(dims, product) {
    const group = new THREE.Group();
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(dims.width, dims.height, dims.depth),
      new THREE.MeshStandardMaterial({ color: 0xb17a43, roughness: 0.7, transparent: true, opacity: 0.4 })
    );
    box.position.y = dims.height / 2;
    box.castShadow = false;
    box.receiveShadow = false;
    group.add(box);
    group.name = product.title || 'Lumira piece';
    return group;
  }

  loadModel(item) {
    this.loader.load(item.product.glb_url, (gltf) => {
      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      model.scale.set(
        item.width / Math.max(size.x, 0.001),
        item.height / Math.max(size.y, 0.001),
        item.depth / Math.max(size.z, 0.001)
      );
      const scaledBox = new THREE.Box3().setFromObject(model);
      const center = scaledBox.getCenter(new THREE.Vector3());
      model.position.x -= center.x;
      model.position.z -= center.z;
      model.position.y -= scaledBox.min.y;
      model.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = false;
          node.receiveShadow = false;
        }
      });
      item.mesh.clear();
      item.mesh.add(model);
      item.mesh.userData.item = item;
    }, undefined, () => {
      this.setStatus(`Could not load model for ${item.product.title}; true-scale footprint remains visible.`);
    });
  }

  previewProduct(product) {
    this.preview.src = product.glb_url || '';
    if (product.usdz_url) this.preview.setAttribute('ios-src', product.usdz_url);
    const dims = product.trueSizeFt || dimsToFeet(product);
    this.previewMeta.textContent = `${product.title} | ${money(product.price)} | ${dims ? dims.label : 'dimensions needed'}`;
  }

  savePiece(product) {
    const key = String(product.variant_id || product.id);
    this.saved = [product, ...this.saved.filter((row) => String(row.variant_id || row.id) !== key)].sort(sortPlaceable).slice(0, 60);
    localStorage.setItem(SAVED_KEY, JSON.stringify(this.saved));
    const customerId = this.root.dataset.customerId;
    if (customerId) {
      fetch(`${this.root.dataset.sophiBase}/sophi/saved-pieces/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, product })
      }).catch(() => null);
    }
    this.setStatus(`${product.title} saved to My Pieces.`);
    if (this.mode === 'saved') this.renderProducts();
  }

  readLocalSaved() {
    try {
      const rows = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
      return Array.isArray(rows) ? rows : [];
    } catch (error) {
      return [];
    }
  }

  async loadSavedFromShopify() {
    const customerId = this.root.dataset.customerId;
    if (!customerId) return;
    const data = await fetch(`${this.root.dataset.sophiBase}/sophi/saved-pieces/${customerId}`).then((res) => res.ok ? res.json() : null).catch(() => null);
    if (data && Array.isArray(data.pieces)) {
      const merged = [...data.pieces, ...this.saved];
      const seen = new Set();
      this.saved = merged.filter((row) => {
        const key = String(row.variant_id || row.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).sort(sortPlaceable);
      localStorage.setItem(SAVED_KEY, JSON.stringify(this.saved));
    }
  }

  onPointerDown(event) {
    if (this.controls.autoRotate) this.pauseAutoRotate('Auto-rotate paused. Continue from this view.');
    this.setPointer(event);
    const hits = this.raycaster.intersectObjects(this.itemGroup.children, true);
    const hit = hits.find((row) => row.object.parent);
    if (!hit) {
      this.selected = null;
      this.updateSelection();
      return;
    }
    let node = hit.object;
    while (node && !node.userData.item) node = node.parent;
    if (!node) return;
    this.selected = node.userData.item;
    this.dragging = true;
    this.controls.enabled = false;
    this.canvas.classList.add('is-dragging');
    this.canvas.setPointerCapture(event.pointerId);
    const point = this.floorPoint(event);
    if (point) {
      this.dragOffset.copy(this.selected.mesh.position).sub(point);
      this.lastDragPosition.copy(this.selected.mesh.position);
    }
    this.updateSelection();
  }

  onPointerMove(event) {
    if (!this.dragging || !this.selected) return;
    event.preventDefault();
    const point = this.floorPoint(event);
    if (!point) return;
    this.lastDragPosition.copy(this.selected.mesh.position);
    this.selected.mesh.position.x = point.x + this.dragOffset.x;
    this.selected.mesh.position.z = point.z + this.dragOffset.z;
    this.constrainItem(this.selected);
  }

  onPointerUp() {
    if (this.selected && this.collides(this.selected)) {
      this.selected.mesh.position.copy(this.lastDragPosition);
      this.setStatus(`${this.selected.product.title} overlaps another piece. I moved it back slightly.`);
    }
    this.dragging = false;
    this.controls.enabled = true;
    this.canvas.classList.remove('is-dragging');
  }

  setPointer(event) {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
  }

  floorPoint(event) {
    this.setPointer(event);
    const point = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.floorPlane, point);
  }

  footprint(item) {
    const rotated = Math.abs(Math.sin(item.mesh.rotation.y)) > 0.5;
    return {
      width: rotated ? item.depth : item.width,
      depth: rotated ? item.width : item.depth
    };
  }

  constrainItem(item) {
    const foot = this.footprint(item);
    const snap = 0.35;
    const halfW = this.room.width / 2 - foot.width / 2;
    const halfL = this.room.length / 2 - foot.depth / 2;
    item.mesh.position.x = Math.max(-halfW, Math.min(halfW, item.mesh.position.x));
    item.mesh.position.z = Math.max(-halfL, Math.min(halfL, item.mesh.position.z));
    if (Math.abs(item.mesh.position.x - halfW) < snap) item.mesh.position.x = halfW;
    if (Math.abs(item.mesh.position.x + halfW) < snap) item.mesh.position.x = -halfW;
    if (Math.abs(item.mesh.position.z - halfL) < snap) item.mesh.position.z = halfL;
    if (Math.abs(item.mesh.position.z + halfL) < snap) item.mesh.position.z = -halfL;
  }

  constrainAll() {
    this.placed.forEach((item) => this.constrainItem(item));
  }

  collides(item) {
    const a = this.box2(item);
    return this.placed.some((other) => {
      if (other === item) return false;
      const b = this.box2(other);
      return a.minX < b.maxX && a.maxX > b.minX && a.minZ < b.maxZ && a.maxZ > b.minZ;
    });
  }

  box2(item) {
    const foot = this.footprint(item);
    return {
      minX: item.mesh.position.x - foot.width / 2,
      maxX: item.mesh.position.x + foot.width / 2,
      minZ: item.mesh.position.z - foot.depth / 2,
      maxZ: item.mesh.position.z + foot.depth / 2
    };
  }

  rotateSelected(degrees) {
    if (!this.selected) return;
    const before = this.selected.mesh.rotation.y;
    this.selected.mesh.rotation.y += THREE.MathUtils.degToRad(degrees);
    this.constrainItem(this.selected);
    if (this.collides(this.selected)) this.selected.mesh.rotation.y = before;
    this.updateCapacity();
  }

  toggleAutoRotate() {
    this.controls.autoRotate = !this.controls.autoRotate;
    this.syncAutoRotateButton();
    this.setStatus(this.controls.autoRotate ? 'Auto-rotate is on.' : 'Auto-rotate is off. Interior view is locked.');
  }

  pauseAutoRotate(message = '') {
    if (!this.controls.autoRotate) return;
    this.controls.autoRotate = false;
    this.syncAutoRotateButton();
    if (message) this.setStatus(message);
  }

  syncAutoRotateButton() {
    if (!this.autoRotateButton) return;
    this.autoRotateButton.setAttribute('aria-pressed', this.controls.autoRotate ? 'true' : 'false');
    this.autoRotateButton.classList.toggle('is-active', this.controls.autoRotate);
  }

  snapSelected() {
    if (!this.selected) return;
    const item = this.selected;
    const foot = this.footprint(item);
    const distances = [
      { side: 'left', value: Math.abs(item.mesh.position.x + this.room.width / 2 - foot.width / 2), x: -this.room.width / 2 + foot.width / 2, rot: Math.PI / 2 },
      { side: 'right', value: Math.abs(this.room.width / 2 - item.mesh.position.x - foot.width / 2), x: this.room.width / 2 - foot.width / 2, rot: -Math.PI / 2 },
      { side: 'back', value: Math.abs(item.mesh.position.z + this.room.length / 2 - foot.depth / 2), z: -this.room.length / 2 + foot.depth / 2, rot: 0 },
      { side: 'front', value: Math.abs(this.room.length / 2 - item.mesh.position.z - foot.depth / 2), z: this.room.length / 2 - foot.depth / 2, rot: Math.PI }
    ].sort((a, b) => a.value - b.value)[0];
    if (distances.x !== undefined) item.mesh.position.x = distances.x;
    if (distances.z !== undefined) item.mesh.position.z = distances.z;
    item.mesh.rotation.y = distances.rot;
    this.constrainItem(item);
    this.updateCapacity();
    this.setStatus(`${item.product.title} snapped to ${distances.side} wall.`);
  }

  removeSelected() {
    if (!this.selected) return;
    this.itemGroup.remove(this.selected.mesh);
    this.placed = this.placed.filter((item) => item !== this.selected);
    this.selected = null;
    this.updateSelection();
    this.updateCapacity();
  }

  updateSelection() {
    this.placed.forEach((item) => {
      item.mesh.traverse((node) => {
        if (node.isMesh && node.material && node.material.emissive) node.material.emissive.setHex(item === this.selected ? 0x2a4c43 : 0x000000);
      });
    });
    this.empty.hidden = this.placed.length > 0;
    this.pieceCount.textContent = `${this.placed.length} placed`;
    if (this.selected) this.previewProduct(this.selected.product);
    if (this.mode === 'room') this.renderProducts();
    this.updateCapacity();
  }

  floorArea() {
    return Math.max(1, this.room.width * this.room.length);
  }

  itemArea(item) {
    const foot = item.mesh ? this.footprint(item) : { width: item.width, depth: item.depth };
    return Math.max(0, foot.width * foot.depth);
  }

  coverageRatio(extraDims = null) {
    const current = this.placed.reduce((sum, item) => sum + this.itemArea(item), 0);
    const extra = extraDims ? Math.max(0, extraDims.width * extraDims.depth) : 0;
    return (current + extra) / this.floorArea();
  }

  updateCapacity(forcedRatio = null) {
    if (!this.capacityLabel || !this.capacityBar) return;
    const ratio = forcedRatio === null ? this.coverageRatio() : forcedRatio;
    const percent = Math.max(0, Math.round(ratio * 100));
    const targetPercent = Math.round(this.capacityTarget * 100);
    this.capacityLabel.textContent = `${percent}% floor coverage / ${targetPercent}% Sophi target`;
    this.capacityBar.style.width = `${Math.min(100, percent)}%`;
    const root = this.capacityBar.closest('.lrb-capacity');
    if (root) {
      root.classList.toggle('is-warning', ratio >= this.capacityTarget * 0.85 && ratio < this.capacityTarget);
      root.classList.toggle('is-full', ratio >= this.capacityTarget);
    }
  }

  serialize() {
    return {
      room: this.room,
      placed: this.placed.map((item) => ({
        product: item.product,
        position: { x: item.mesh.position.x, z: item.mesh.position.z },
        rotation: item.mesh.rotation.y
      }))
    };
  }

  saveLayout() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.serialize()));
    this.setStatus('Layout saved in this browser.');
  }

  restoreLayout(showStatus) {
    let payload = null;
    try {
      payload = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      payload = null;
    }
    if (!payload) return;
    this.itemGroup.clear();
    this.placed = [];
    Object.assign(this.room, payload.room || {});
    this.room.width = feet(this.room.width, 12);
    this.room.length = feet(this.room.length, 14);
    this.room.height = feet(this.room.height, 8);
    $(this.root, '[data-lrb-width]').value = this.room.width;
    $(this.root, '[data-lrb-length]').value = this.room.length;
    $(this.root, '[data-lrb-height]').value = this.room.height;
    $(this.root, '[data-lrb-wall]').value = this.room.wall || '#f2eee7';
    $(this.root, '[data-lrb-floor]').value = this.room.floor || 'light-wood';
    this.generateRoom();
    (payload.placed || []).forEach((row) => {
      const product = Object.assign({}, row.product);
      const dims = dimsToFeet(product);
      if (!dims) return;
      product.trueSizeFt = dims;
      this.placeProduct(product, row.position || {});
      if (this.selected) this.selected.mesh.rotation.y = Number(row.rotation || 0);
    });
    this.updateCapacity();
    if (showStatus) this.setStatus('Saved layout restored.');
  }

  checkout() {
    const counts = new Map();
    this.placed.forEach((item) => {
      const variant = String(item.product.variant_id || '').trim();
      if (variant) counts.set(variant, (counts.get(variant) || 0) + 1);
    });
    if (!counts.size) {
      this.setStatus('Add at least one piece before checkout.');
      return;
    }
    const parts = Array.from(counts.entries()).map(([variant, qty]) => `${variant}:${qty}`).join(',');
    window.location.href = `/cart/${parts}`;
  }

  resize() {
    const rect = this.stage.getBoundingClientRect();
    const width = Math.max(rect.width, 320);
    const height = Math.max(rect.height, 360);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  setInteriorCamera() {
    const eyeY = Math.min(this.room.height - 0.7, Math.max(4.8, this.room.height * 0.62));
    const radius = Math.max(2.2, Math.min(this.room.width, this.room.length) * 0.28);
    this.camera.position.set(0, eyeY, Math.min(this.room.length / 2 - 1, radius));
    this.controls.target.set(0, Math.min(this.room.height - 1, Math.max(1.7, this.room.height * 0.28)), -Math.min(1.2, this.room.length * 0.08));
    this.controls.minDistance = 1.2;
    this.controls.maxDistance = Math.max(2.2, Math.min(this.room.width, this.room.length) * 0.38);
    this.constrainCamera();
  }

  updateDimensionCopy() {
    if (this.dimensionStatus) this.dimensionStatus.textContent = roomLabel(this.room);
  }

  showRoomReady() {
    if (!this.roomReady) return;
    this.roomReady.textContent = `Your ${formatFeet(this.room.width)} x ${formatFeet(this.room.length)} room is ready - add pieces.`;
    this.roomReady.classList.add('is-visible');
    window.clearTimeout(this.roomReadyTimer);
    this.roomReadyTimer = window.setTimeout(() => {
      this.roomReady.classList.remove('is-visible');
    }, 3200);
  }

  setMode(mode) {
    this.mode = mode || 'saved';
    this.root.querySelectorAll('[data-lrb-tab]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.lrbTab === this.mode);
    });
  }

  constrainCamera() {
    const margin = 0.65;
    const halfW = Math.max(margin, this.room.width / 2 - margin);
    const halfL = Math.max(margin, this.room.length / 2 - margin);
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    this.camera.position.x = clamp(this.camera.position.x, -halfW, halfW);
    this.camera.position.z = clamp(this.camera.position.z, -halfL, halfL);
    this.camera.position.y = clamp(this.camera.position.y, 1.2, Math.max(1.4, this.room.height - 0.35));
    this.controls.target.x = clamp(this.controls.target.x, -halfW, halfW);
    this.controls.target.z = clamp(this.controls.target.z, -halfL, halfL);
    this.controls.target.y = clamp(this.controls.target.y, 0.8, Math.max(1, this.room.height - 0.5));
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    this.constrainCamera();
    this.controls.update(this.clock.getDelta());
    this.renderer.render(this.scene, this.camera);
  }

  setStatus(message) {
    this.status.textContent = message;
  }
}

document.querySelectorAll('[data-room-builder]').forEach((root) => {
  const builder = new RoomBuilder(root);
  window.LumiraRoomBuilderDebug = builder;
  root.removeAttribute('data-room-builder');
});
